/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import PlaybookListQuery$parameters from '@jira/playbooks__list-screen/src/__generated__/PlaybookListQuery$parameters.ts';
import type {
	JiraPlaybookFilter,
	JiraPlaybookStateField,
	PlaybookListQuery,
} from '@jira/playbooks__list-screen/src/__generated__/PlaybookListQuery.graphql.ts';

type Queries = { PlaybookListQuery: PlaybookListQuery };

type ExtraProps = {
	projectKey: string | null | undefined;
};

type PlaybookListOnlyViewEntryPointComponent = EntryPointComponent<Queries, {}, {}, ExtraProps>;

const playbookListOnlyViewEntryPoint: EntryPoint<
	PlaybookListOnlyViewEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForInteraction<PlaybookListOnlyViewEntryPointComponent>(
		/* webpackChunkName: "async-spa-apps-playbooks-list-only" */ './index.tsx',
	),
	getPreloadProps: ({ context, tenantContext }: EntryPointRouteParams) => {
		const { projectKey } = context.match.params;
		const { name = '', status } = context.query;

		if (!projectKey) {
			throw new Error('projectKey is required');
		}

		return {
			queries: {
				PlaybookListQuery: {
					parameters: PlaybookListQuery$parameters,
					variables: {
						cloudId: tenantContext.cloudId,
						projectKey,
						first: PLAYBOOK_LIST_FIRST_COUNT,
						filter: getFilters(name, status),
						isFetchingLabels: fg('phantom-3292-playbook-labels'),
						isFetchingWorkType: fg('phantom-5397_playbook_work_type_column'),
					},
				},
			},
			extraProps: {
				projectKey: context.match.params.projectKey,
			},
		};
	},
});

export default playbookListOnlyViewEntryPoint;

const getFilters = (name: string, status: string): JiraPlaybookFilter | null => {
	if (fg('phantom-playbook-save-as-draft')) {
		const filter: FilterType = {};
		const state = getStateFromStatus(status);
		if (name) {
			filter.name = name;
		}
		if (state) {
			filter.state = state;
		}
		return Object.values(filter).some(Boolean) ? filter : null;
	}
	return name ? { name } : null;
};

const PLAYBOOK_LIST_FIRST_COUNT = 20;

type FilterType = { name?: string; state?: JiraPlaybookStateField } | null;

const getStateFromStatus = (status: string): JiraPlaybookStateField | undefined => {
	switch (status) {
		case 'on':
			return 'ENABLED';
		case 'off':
			return 'DISABLED';
		case 'draft':
			return 'DRAFT';
		default:
			return undefined;
	}
};
